import { useEffect, useState, useCallback, useMemo } from 'react';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Link, Navigate } from 'react-router-dom';
import {
  MessageSquare, ArrowLeft, RefreshCw, Trash2, Check, Loader2, Image as ImageIcon,
  Bug, Sparkles, HelpCircle, Heart, MoreHorizontal, X, CheckSquare, Square,
  Mail, MailWarning, MailCheck, Send, FileText,
} from 'lucide-react';

const CATEGORY_META = {
  bug:       { label: 'Bug',        Icon: Bug,             cls: 'bg-red-50 text-red-700 ring-red-200' },
  feature:   { label: 'Feature',    Icon: Sparkles,        cls: 'bg-gold/10 text-gold ring-gold/30' },
  confusing: { label: 'Confusing',  Icon: HelpCircle,      cls: 'bg-beige text-navy ring-navy/10' },
  love:      { label: 'Love',       Icon: Heart,           cls: 'bg-green/10 text-green ring-green/20' },
  other:     { label: 'Other',      Icon: MoreHorizontal,  cls: 'bg-beige text-navy ring-navy/10' },
};

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [byCat, setByCat] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open'); // open | resolved | all
  const [busyId, setBusyId] = useState(null);
  const [screenshot, setScreenshot] = useState(null); // { id, dataUrl }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(null); // { kind: 'selected'|'resolved'|'all', count }
  const [resolveTarget, setResolveTarget] = useState(null); // { id, ref, subject, user_email, public_resolution, internal_notes }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/feedback');
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setByCat(r.data?.by_category || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error('Failed to load feedback');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return items.filter(f => {
      if (catFilter !== 'all' && f.category !== catFilter) return false;
      if (statusFilter === 'open' && f.resolved) return false;
      if (statusFilter === 'resolved' && !f.resolved) return false;
      return true;
    });
  }, [items, catFilter, statusFilter]);

  // Prune selection whenever the filtered set changes so hidden rows don't stay selected.
  useEffect(() => {
    setSelectedIds(prev => {
      const visible = new Set(filtered.map(f => f.id));
      const next = new Set();
      prev.forEach(id => { if (visible.has(id)) next.add(id); });
      return next;
    });
  }, [filtered]);

  const allVisibleSelected = filtered.length > 0 && filtered.every(f => selectedIds.has(f.id));
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(f => f.id)));
    }
  };

  const toggleResolved = async (f) => {
    // If closing (reopening) a resolved item — no modal needed.
    if (f.resolved) {
      setBusyId(f.id);
      try {
        await http.patch(`/admin/feedback/${f.id}`, { resolved: false });
        setItems(prev => prev.map(x => x.id === f.id ? { ...x, resolved: false } : x));
        toast.success('Reopened');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Update failed');
      } finally { setBusyId(null); }
      return;
    }
    // Marking as resolved — open the resolution modal (requires public summary).
    setResolveTarget({
      id: f.id,
      ref: f.ref,
      subject: f.subject,
      user_email: f.user_email,
      public_resolution: f.public_resolution || '',
      internal_notes: f.internal_notes || '',
    });
  };

  const submitResolve = async () => {
    if (!resolveTarget) return;
    const pr = (resolveTarget.public_resolution || '').trim();
    if (!pr && !user?.bootstrap_owner) {
      toast.error('A public resolution summary is required.');
      return;
    }
    setBusyId(resolveTarget.id);
    try {
      const r = await http.patch(`/admin/feedback/${resolveTarget.id}`, {
        resolved: true,
        public_resolution: pr || null,
        internal_notes: resolveTarget.internal_notes || null,
      });
      setItems(prev => prev.map(x => x.id === resolveTarget.id ? {
        ...x,
        resolved: true,
        public_resolution: pr,
        internal_notes: resolveTarget.internal_notes || null,
        emails: { ...(x.emails || {}), resolution: r.data?.resolution_email || (x.emails || {}).resolution },
      } : x));
      setResolveTarget(null);
      if (r.data?.resolution_email_sent) {
        toast.success('Resolved. Notification email sent.');
      } else if (resolveTarget.user_email) {
        toast.warning('Resolved — but the email did not send. You can retry from the item.');
      } else {
        toast.success('Resolved. (Anonymous submitter — no email sent.)');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };

  const resendEmail = async (f, kind) => {
    setBusyId(f.id + ':' + kind);
    try {
      const r = await http.post(`/admin/feedback/${f.id}/resend/${kind}`);
      setItems(prev => prev.map(x => x.id === f.id ? {
        ...x,
        emails: { ...(x.emails || {}), [kind]: r.data?.email || (x.emails || {})[kind] },
      } : x));
      toast.success(r.data?.sent ? 'Email resent.' : 'Retry failed — see status.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally { setBusyId(null); }
  };

  const deleteItem = async (f) => {
    if (!window.confirm('Delete this feedback permanently?')) return;
    setBusyId(f.id);
    try {
      await http.delete(`/admin/feedback/${f.id}`);
      setItems(prev => prev.filter(x => x.id !== f.id));
      setTotal(t => Math.max(0, t - 1));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusyId(null); }
  };

  const viewScreenshot = async (id) => {
    try {
      const r = await http.get(`/admin/feedback/${id}/screenshot`);
      setScreenshot({ id, dataUrl: r.data?.screenshot_base64 });
    } catch (e) {
      toast.error('No screenshot available');
    }
  };

  const runBulkDelete = async () => {
    if (!confirmBulk) return;
    setBulkBusy(true);
    try {
      let body;
      if (confirmBulk.kind === 'selected') {
        body = { ids: Array.from(selectedIds) };
      } else if (confirmBulk.kind === 'resolved') {
        body = { scope: 'resolved' };
      } else {
        body = { scope: 'all' };
      }
      const r = await http.post('/admin/feedback/bulk-delete', body);
      toast.success(`Deleted ${r.data?.deleted ?? 0} item${r.data?.deleted === 1 ? '' : 's'}`);
      setConfirmBulk(null);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkBusy(false); }
  };

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const openCount = items.filter(i => !i.resolved).length;
  const resolvedCount = items.filter(i => i.resolved).length;

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-5 lg:p-10 space-y-6" data-testid="admin-feedback-page">
      <Link to="/app/admin" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="back-to-admin">
        <ArrowLeft size={14}/> Back to Admin
      </Link>

      <div className="flex items-center gap-3">
        <MessageSquare className="text-gold" size={22}/>
        <div>
          <h1 className="font-heading text-3xl">Beta Feedback</h1>
          <p className="text-sm text-navy/55">Bug reports and feedback from beta users. Mark resolved as you address them.</p>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl p-4 card-soft">
          <p className="text-[11px] uppercase tracking-widest text-navy/55">Total</p>
          <p className="font-heading text-2xl mt-1">{total}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 card-soft">
          <p className="text-[11px] uppercase tracking-widest text-navy/55">Open</p>
          <p className="font-heading text-2xl mt-1 text-red-700">{openCount}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 card-soft">
          <p className="text-[11px] uppercase tracking-widest text-navy/55">Resolved</p>
          <p className="font-heading text-2xl mt-1 text-green">{resolvedCount}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 card-soft">
          <p className="text-[11px] uppercase tracking-widest text-navy/55">Categories</p>
          <p className="text-[11px] text-navy/60 mt-2 leading-relaxed">
            {byCat.length === 0 ? '—' : byCat.map(c => `${c.category}: ${c.count}`).join(' · ')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 card-soft flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          <FilterPill active={statusFilter === 'open'} onClick={() => setStatusFilter('open')} testid="filter-status-open">Open</FilterPill>
          <FilterPill active={statusFilter === 'resolved'} onClick={() => setStatusFilter('resolved')} testid="filter-status-resolved">Resolved</FilterPill>
          <FilterPill active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} testid="filter-status-all">All</FilterPill>
        </div>
        <div className="h-6 w-px bg-navy/10 hidden lg:block"/>
        <div className="flex flex-wrap gap-2">
          <FilterPill active={catFilter === 'all'} onClick={() => setCatFilter('all')} testid="filter-cat-all">All types</FilterPill>
          {Object.entries(CATEGORY_META).map(([id, meta]) => (
            <FilterPill key={id} active={catFilter === id} onClick={() => setCatFilter(id)} testid={`filter-cat-${id}`}>
              <meta.Icon size={12}/> {meta.label}
            </FilterPill>
          ))}
        </div>
        <button onClick={load} className="ml-auto px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 transition-smooth flex items-center gap-2" data-testid="feedback-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      {/* Bulk action bar */}
      <div className="bg-white rounded-2xl p-3 lg:p-4 card-soft flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
        <button
          onClick={toggleAllVisible}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-2 text-sm text-navy/80 hover:text-navy disabled:opacity-40"
          data-testid="select-all-toggle"
        >
          {allVisibleSelected ? <CheckSquare size={16} className="text-gold"/> : <Square size={16}/>}
          {allVisibleSelected ? 'Deselect all' : `Select all (${filtered.length})`}
        </button>
        {selectedIds.size > 0 && (
          <span className="text-xs text-navy/60" data-testid="selected-count">
            {selectedIds.size} selected
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            onClick={() => setConfirmBulk({ kind: 'selected', count: selectedIds.size })}
            disabled={selectedIds.size === 0}
            className="text-[12px] px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition-smooth inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bulk-delete-selected"
          >
            <Trash2 size={12}/> Delete selected
          </button>
          <button
            onClick={() => setConfirmBulk({ kind: 'resolved', count: items.filter(i => i.resolved).length })}
            disabled={!items.some(i => i.resolved)}
            className="text-[12px] px-3 py-1.5 rounded-full bg-beige text-navy hover:bg-[#EAE5D9] transition-smooth inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="bulk-delete-resolved"
          >
            <Trash2 size={12}/> Delete all resolved
          </button>
          <button
            onClick={() => setConfirmBulk({ kind: 'all', count: total })}
            disabled={total === 0}
            className="text-[12px] px-3 py-1.5 rounded-full text-red-700 hover:bg-red-50 transition-smooth inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="bulk-delete-all"
          >
            <Trash2 size={12}/> Delete everything
          </button>
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        {loading && (
          <div className="bg-white rounded-2xl p-8 text-center text-navy/45 card-soft">
            <Loader2 className="inline animate-spin" size={18}/>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-2xl p-8 text-center text-navy/50 card-soft" data-testid="feedback-empty">
            No feedback matches these filters.
          </div>
        )}
        {!loading && filtered.map((f) => {
          const meta = CATEGORY_META[f.category] || CATEGORY_META.other;
          const isSelected = selectedIds.has(f.id);
          return (
            <div key={f.id} data-testid={`feedback-item-${f.id}`} className={`bg-white rounded-2xl p-5 card-soft ${f.resolved ? 'opacity-70' : ''} ${isSelected ? 'ring-2 ring-gold' : ''}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => toggleOne(f.id)}
                    className="p-0.5 -m-0.5 text-navy/60 hover:text-navy"
                    data-testid={`select-${f.id}`}
                    aria-label={isSelected ? 'Deselect' : 'Select'}
                  >
                    {isSelected ? <CheckSquare size={16} className="text-gold"/> : <Square size={16}/>}
                  </button>
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold ring-1 ${meta.cls}`}>
                    <meta.Icon size={10}/> {meta.label}
                  </span>
                  {f.ref && (
                    <span className="text-[10px] font-mono text-navy/55 font-semibold" data-testid={`feedback-ref-${f.id}`}>
                      {f.ref}
                    </span>
                  )}
                  {f.resolved && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold ring-1 bg-green/10 text-green ring-green/20">
                      <Check size={10}/> Resolved
                    </span>
                  )}
                  <p className="text-xs text-navy/60">{f.user_email || <span className="italic text-navy/40">anonymous</span>}</p>
                </div>
                <p className="text-[10px] text-navy/40">{fmt(f.created_at)}</p>
              </div>

              {f.subject && (
                <p className="mt-3 text-sm font-medium text-navy" data-testid={`feedback-subject-${f.id}`}>
                  {f.subject}
                </p>
              )}
              <p className="text-sm text-navy/85 mt-1 leading-relaxed whitespace-pre-wrap">{f.message}</p>

              {f.public_resolution && (
                <div className="mt-3 p-3 bg-beige rounded-xl border-l-2 border-green" data-testid={`resolution-summary-${f.id}`}>
                  <p className="text-[10px] uppercase tracking-widest text-navy/55 font-semibold">Public resolution</p>
                  <p className="text-sm text-navy/85 mt-1 whitespace-pre-wrap">{f.public_resolution}</p>
                </div>
              )}

              {(f.page_url || f.user_agent) && (
                <p className="text-[10px] text-navy/40 mt-2">
                  {f.page_url && <>Page: <span className="font-mono">{f.page_url}</span></>}
                  {f.page_url && f.user_agent && <> · </>}
                  {f.user_agent && <>{f.user_agent.slice(0, 100)}</>}
                </p>
              )}

              {/* Email delivery status pills */}
              {f.user_email && (f.emails?.submission || f.emails?.resolution) && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`email-status-${f.id}`}>
                  <EmailStatusPill kind="submission" status={f.emails?.submission}
                    onResend={() => resendEmail(f, 'submission')}
                    busy={busyId === f.id + ':submission'} />
                  {f.resolved && (
                    <EmailStatusPill kind="resolution" status={f.emails?.resolution}
                      onResend={() => resendEmail(f, 'resolution')}
                      busy={busyId === f.id + ':resolution'} />
                  )}
                </div>
              )}

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => toggleResolved(f)}
                  disabled={busyId === f.id}
                  className={`text-[12px] px-3 py-1.5 rounded-full transition-smooth disabled:opacity-50 ${
                    f.resolved
                      ? 'bg-beige text-navy hover:bg-[#EAE5D9]'
                      : 'bg-green/10 text-green hover:bg-green/20'
                  }`}
                  data-testid={`toggle-resolved-${f.id}`}
                >
                  {busyId === f.id ? <Loader2 size={12} className="inline animate-spin"/> : <Check size={12} className="inline mr-1"/>}
                  {f.resolved ? 'Reopen' : 'Mark resolved'}
                </button>
                <button
                  onClick={() => viewScreenshot(f.id)}
                  className="text-[12px] px-3 py-1.5 rounded-full bg-beige text-navy hover:bg-[#EAE5D9] transition-smooth inline-flex items-center gap-1"
                  data-testid={`view-screenshot-${f.id}`}
                >
                  <ImageIcon size={12}/> Screenshot
                </button>
                <button
                  onClick={() => deleteItem(f)}
                  disabled={busyId === f.id}
                  className="ml-auto text-[12px] px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50 transition-smooth inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`delete-feedback-${f.id}`}
                >
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Resolve modal — requires public resolution summary */}
      {resolveTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => busyId !== resolveTarget.id && setResolveTarget(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="resolve-dialog">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-green/10 flex items-center justify-center shrink-0">
                <Check size={16} className="text-green"/>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-xl text-navy">Mark {resolveTarget.ref || 'this feedback'} as resolved</h3>
                <p className="mt-1 text-sm text-navy/60">
                  {resolveTarget.user_email
                    ? <>An email will be sent to <span className="font-medium text-navy/80">{resolveTarget.user_email}</span> with the public resolution summary below.</>
                    : <>No email will be sent (anonymous submission). The resolution summary is kept for the audit trail.</>}
                </p>
              </div>
            </div>

            <label className="block mt-5 text-[11px] uppercase tracking-widest text-navy/55 font-semibold">
              Public resolution summary <span className="text-red-600">*</span>
            </label>
            <textarea
              data-testid="public-resolution-input"
              value={resolveTarget.public_resolution}
              onChange={(e) => setResolveTarget({ ...resolveTarget, public_resolution: e.target.value })}
              placeholder="Explain what was fixed / decided. This text goes into the email to the user — do NOT include internal notes."
              rows={4}
              maxLength={2000}
              className="mt-1 w-full bg-white border border-navy/10 rounded-2xl p-3 text-sm text-navy leading-relaxed focus:outline-none focus:ring-2 focus:ring-green resize-none"
            />
            <p className="text-[10px] text-navy/40 mt-1">Visible to the user in their resolution email.</p>

            <label className="block mt-4 text-[11px] uppercase tracking-widest text-navy/55 font-semibold">Internal notes <span className="normal-case tracking-normal font-normal text-navy/40">(admins only, never emailed)</span></label>
            <textarea
              data-testid="internal-notes-input"
              value={resolveTarget.internal_notes}
              onChange={(e) => setResolveTarget({ ...resolveTarget, internal_notes: e.target.value })}
              placeholder="Optional debugging notes, ticket refs, staff comments — kept private."
              rows={3}
              maxLength={5000}
              className="mt-1 w-full bg-white border border-navy/10 rounded-2xl p-3 text-sm text-navy leading-relaxed focus:outline-none focus:ring-2 focus:ring-navy resize-none"
            />

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setResolveTarget(null)}
                disabled={busyId === resolveTarget.id}
                className="px-4 py-2 rounded-lg text-sm text-navy/70 hover:bg-navy/5 disabled:opacity-50"
                data-testid="resolve-cancel"
              >Cancel</button>
              <button
                onClick={submitResolve}
                disabled={busyId === resolveTarget.id || (!resolveTarget.public_resolution.trim() && !user?.bootstrap_owner)}
                className="px-4 py-2 rounded-lg bg-green text-white text-sm font-medium hover:bg-green/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="resolve-submit"
              >
                {busyId === resolveTarget.id && <Loader2 size={14} className="animate-spin"/>}
                {busyId === resolveTarget.id ? 'Resolving…' : 'Mark resolved & send email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation */}
      {confirmBulk && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !bulkBusy && setConfirmBulk(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-confirm-dialog">
            <h2 className="font-heading text-xl">
              {confirmBulk.kind === 'selected' && `Delete ${confirmBulk.count} selected item${confirmBulk.count === 1 ? '' : 's'}?`}
              {confirmBulk.kind === 'resolved' && `Delete all ${confirmBulk.count} resolved item${confirmBulk.count === 1 ? '' : 's'}?`}
              {confirmBulk.kind === 'all' && `Delete ALL ${confirmBulk.count} feedback items?`}
            </h2>
            <p className="mt-3 text-[13px] text-navy/60 leading-relaxed">
              {confirmBulk.kind === 'all'
                ? 'This permanently removes every feedback row — open and resolved. Cannot be undone.'
                : 'This permanently removes the selected items. Cannot be undone.'}
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmBulk(null)}
                disabled={bulkBusy}
                className="px-4 py-2 rounded-lg text-sm text-navy/70 hover:bg-navy/5 disabled:opacity-50"
                data-testid="bulk-cancel"
              >Cancel</button>
              <button
                onClick={runBulkDelete}
                disabled={bulkBusy}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="bulk-confirm"
              >
                {bulkBusy && <Loader2 size={14} className="animate-spin"/>}
                {bulkBusy ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot modal */}
      {screenshot && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setScreenshot(null)}>
          <div className="max-w-3xl w-full max-h-[90vh] overflow-auto bg-white rounded-2xl p-4 relative" onClick={(e) => e.stopPropagation()} data-testid="screenshot-modal">
            <button
              onClick={() => setScreenshot(null)}
              className="absolute top-3 right-3 text-navy/50 hover:text-navy p-1"
              data-testid="close-screenshot"
            >
              <X size={18}/>
            </button>
            {screenshot.dataUrl ? (
              <img src={screenshot.dataUrl} alt="Feedback screenshot" className="w-full h-auto rounded-lg"/>
            ) : (
              <p className="text-navy/50 text-sm p-8 text-center">No screenshot data.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-smooth ring-1 ${
        active
          ? 'bg-navy text-white ring-navy'
          : 'bg-white text-navy/70 hover:text-navy ring-navy/10 hover:ring-navy/30'
      }`}
    >
      {children}
    </button>
  );
}

function EmailStatusPill({ kind, status, onResend, busy }) {
  const sent = status?.sent;
  const skipped = status?.skipped;
  const err = status?.error;
  const at = status?.at ? new Date(status.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
  const label = kind === 'submission' ? 'Confirmation email' : 'Resolution email';

  if (skipped) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-1 font-semibold ring-1 bg-beige text-navy/50 ring-navy/10" title={`Skipped: ${status.reason || 'anonymous'}`}>
        <Mail size={10}/> {label} · skipped
      </span>
    );
  }
  if (sent) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-1 font-semibold ring-1 bg-green/10 text-green ring-green/20" title={at ? `Sent ${at}` : 'Sent'}>
        <MailCheck size={10}/> {label} · sent {at && <span className="normal-case tracking-normal font-normal text-navy/50 ml-1">{at}</span>}
      </span>
    );
  }
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-1 font-semibold ring-1 bg-beige text-navy/50 ring-navy/10">
        <Mail size={10}/> {label} · not sent
      </span>
    );
  }
  // Failure state
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-1 font-semibold ring-1 bg-red-50 text-red-700 ring-red-200"
      title={err || 'Email failed'}
    >
      <MailWarning size={10}/> {label} · failed
      <button
        onClick={onResend}
        disabled={busy}
        className="ml-1 normal-case tracking-normal font-normal underline text-red-700 hover:text-red-900 disabled:opacity-50"
        data-testid={`resend-${kind}`}
      >
        {busy ? <Loader2 size={10} className="inline animate-spin"/> : <><Send size={10} className="inline mr-0.5"/>Resend</>}
      </button>
    </span>
  );
}
